import React, { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import {
  MdHome,
  MdLogout,
  MdPerson,
  MdPhone,
  MdRocketLaunch,
  MdWork,
  MdWorkOutline,
} from 'react-icons/md';

const navIconProps = { size: 20 as const, style: { display: 'block' as const } };
const HomeIcon = () => <MdHome {...navIconProps} />;
const AboutIcon = () => <MdPerson {...navIconProps} />;
const ExperienceIcon = () => <MdWorkOutline {...navIconProps} />;
const SkillsIcon = () => <MdRocketLaunch {...navIconProps} />;
const PortfolioIcon = () => <MdWork {...navIconProps} />;
const ContactIcon = () => <MdPhone {...navIconProps} />;
const LogoutIcon = () => <MdLogout {...navIconProps} />;

interface SidebarContainerProps {
  isOpen?: boolean;
}

const SidebarContainer = styled(motion.aside)<SidebarContainerProps>`
  position: fixed;
  top: 0;
  left: 0;
  height: 100%;
  padding-top: 80px;
  width: 250px;
  background-color: ${({ theme }) => theme.colors.dark};
  color: ${({ theme }) => theme.colors.text};
  z-index: 90;
  overflow-y: auto;
  box-shadow: 2px 0 10px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    transform: translateX(${props => props.isOpen ? '0' : '-100%'});
    width: 100%;
    max-width: 250px;
    transition: transform 0.05s ease;
  }
`;

const NavItem = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  color: ${({ theme }) => theme.colors.textLight};
  text-decoration: none;
  transition: background-color 0.2s;
  border-left: 3px solid transparent;

  &:hover {
    background-color: ${({ theme }) => theme.colors.light};
    color: ${({ theme }) => theme.colors.text};
  }

  &.active {
    background-color: ${({ theme }) => theme.colors.light};
    color: ${({ theme }) => theme.colors.text};
    border-left-color: ${({ theme }) => theme.colors.primary};
  }

  span {
    font-size: 16px;
  }
`;

const LogoutButton = styled(motion.button)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: calc(100% - 40px);
  margin: 16px 20px;
  padding: 14px 20px;
  background: linear-gradient(135deg,rgb(48, 21, 21) 0%, #E41B1B 45%, #B31515 100%);
  color: ${({ theme }) => theme.colors.text};
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 6px 16px rgba(228, 27, 27, 0.28);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    border-radius: 50%;
    background: rgba(252, 232, 232, 0.18);
    transform: translate(-50%, -50%);
    transition: width 0.6s, height 0.6s;
  }
  
  &:hover::before {
    width: 300px;
    height: 300px;
  }
  
  &:hover {
    background: linear-gradient(135deg, #E94444 0%, #B31515 100%);
    box-shadow: 0 10px 24px rgba(228, 27, 27, 0.35);
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  span {
    font-size: 16px;
    position: relative;
    z-index: 1;
  }
  
  @media (max-width: 768px) {
    margin: 12px 16px;
    width: calc(100% - 32px);
    padding: 12px 16px;
  }
`;

const SidebarFooter = styled.div`
  margin-top: auto;
  padding: 16px;
  text-align: center;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textLight};
  border-top: 1px solid rgba(252, 232, 232, 0.1);
`;

const mobileVariants = {
  open: {
    x: 0,
    boxShadow: "2px 0 10px rgba(0, 0, 0, 0.2)",
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 30
    }
  },
  closed: {
    x: "-100%",
    boxShadow: "none",
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 30
    }
  }
};

const Sidebar: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsOpen(!Boolean(window.innerWidth <= 768));
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const handleMobileToggle = () => {
      if (window.innerWidth <= 768) {
        setIsOpen((prev) => !prev);
      }
    };

    window.addEventListener('sidebar:toggle-mobile', handleMobileToggle);
    return () => {
      window.removeEventListener('sidebar:toggle-mobile', handleMobileToggle);
    };
  }, []);

  const handleNavClick = () => {
    if (isMobile) {
      setIsOpen(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    if (isMobile) {
      setIsOpen(false);
    }
    window.location.reload();
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <>
      <SidebarContainer 
        isOpen={isOpen}
        animate={isMobile ? (isOpen ? "open" : "closed") : undefined}
        variants={isMobile ? mobileVariants : undefined}
      >
        <nav>
          <NavItem to="/" onClick={handleNavClick} className={isActive('/') ? 'active' : ''}>
            <HomeIcon />
            <span>{t('nav.home')}</span>
          </NavItem>
          <NavItem to="/about" onClick={handleNavClick} className={isActive('/about') ? 'active' : ''}>
            <AboutIcon />
            <span>{t('nav.about')}</span>
          </NavItem>
          <NavItem to="/experience" onClick={handleNavClick} className={isActive('/experience') ? 'active' : ''}>
            <ExperienceIcon />
            <span>{t('nav.experience')}</span>
          </NavItem>
          <NavItem to="/skills" onClick={handleNavClick} className={isActive('/skills') ? 'active' : ''}>
            <SkillsIcon />
            <span>{t('nav.skills')}</span>
          </NavItem>
          <NavItem to="/portfolio" onClick={handleNavClick} className={isActive('/portfolio') ? 'active' : ''}>
            <PortfolioIcon />
            <span>{t('nav.portfolio')}</span>
          </NavItem>
          <NavItem to="/contact" onClick={handleNavClick} className={isActive('/contact') ? 'active' : ''}>
            <ContactIcon />
            <span>{t('nav.contact')}</span>
          </NavItem>
        </nav>
        
        {isAuthenticated && (
          <LogoutButton
            onClick={handleLogout}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <LogoutIcon />
            <span>{t('common.logout')}</span>
          </LogoutButton>
        )}
        
        <SidebarFooter>
          &copy; {new Date().getFullYear()} Jhusef Lopez
        </SidebarFooter>
      </SidebarContainer>
    </>
  );
};

export default Sidebar; 